import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import type { User, Role } from '@/_types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const createSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    email: z.string().email('Enter a valid email'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    is_active: z.boolean().default(true),
    user_type: z.enum(['organisation', 'system_admin']).default('organisation'),
    // Organisation owner fields
    organization_name: z.string().optional(),
    // System admin fields
    role_ids: z.array(z.string()).default([]),
  })
  .superRefine((data, ctx) => {
    if (data.user_type === 'organisation' && !data.organization_name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Organisation name is required',
        path: ['organization_name'],
      });
    }
  });

const editSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Enter a valid email'),
  is_active: z.boolean().default(true),
  role_ids: z.array(z.string()).default([]),
});

const passwordSchema = z
  .object({
    new_password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm_password: z.string().min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

type CreateFormValues = z.infer<typeof createSchema>;
type EditFormValues = z.infer<typeof editSchema>;
type PasswordFormValues = z.infer<typeof passwordSchema>;

// ---------------------------------------------------------------------------
// UserFormPage
// ---------------------------------------------------------------------------

export default function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Fetch active roles ──────────────────────────────────────────────────
  const { data: rolesData } = useQuery({
    queryKey: ['admin-roles-active'],
    queryFn: async () => {
      const res = await apiClient.get('/roles', { params: { per_page: 100, status: 'active' } });
      return (res.data?.data?.roles ?? []) as Role[];
    },
  });
  const roles: Role[] = rolesData ?? [];

  // ── Fetch existing user when editing ───────────────────────────────────
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: async () => {
      const res = await apiClient.get(`/users/${id}`);
      return (res.data?.data?.user ?? res.data?.data) as User;
    },
    enabled: isEdit,
  });

  // ── Main form (create) ─────────────────────────────────────────────────
  const createForm = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      is_active: true,
      user_type: 'organisation',
      organization_name: '',
      role_ids: [],
    },
  });

  // ── Main form (edit) ───────────────────────────────────────────────────
  const editForm = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: { name: '', email: '', is_active: true, role_ids: [] },
  });

  // ── Password form (edit only) ──────────────────────────────────────────
  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  // ── Populate edit form when user data arrives ──────────────────────────
  useEffect(() => {
    if (isEdit && userData) {
      editForm.reset({
        name: userData.name,
        email: userData.email,
        is_active: userData.is_active,
        role_ids: userData.roles?.map((r) => r.id) ?? [],
      });
    }
  }, [isEdit, userData, editForm]);

  // ── Org-role badge colour helper ──────────────────────────────────────
  const orgRoleBadgeClass = (role: string | null) => {
    if (role === 'owner') return 'bg-violet-100 text-violet-700 hover:bg-violet-100 border-violet-200 capitalize';
    if (role === 'admin') return 'bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200 capitalize';
    return 'capitalize';
  };

  // ── Selected role state helpers (edit form only) ───────────────────────
  const editRoleIds = editForm.watch('role_ids');
  const selectedEditRole = editRoleIds[0] ?? '_none';
  const handleEditRoleChange = (val: string) => {
    editForm.setValue('role_ids', val === '_none' ? [] : [val]);
  };

  // ── Create mutation ────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async (values: CreateFormValues) => {
      const payload: Record<string, unknown> = {
        name: values.name,
        email: values.email,
        password: values.password,
        is_active: values.is_active,
        user_type: values.user_type,
      };

      if (values.user_type === 'organisation') {
        payload.organization_name = values.organization_name;
        payload.plan = 'trial';
      } else {
        payload.role_ids = values.role_ids;
      }

      await apiClient.post('/users', payload);
    },
    onSuccess: () => {
      toast.success('User created');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      navigate('/users');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Edit mutation ──────────────────────────────────────────────────────
  const editMutation = useMutation({
    mutationFn: async (values: EditFormValues) => {
      await apiClient.patch(`/users/${id}`, {
        name: values.name,
        email: values.email,
        is_active: values.is_active,
        role_ids: values.role_ids,
      });
    },
    onSuccess: () => {
      toast.success('User updated');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-user', id] });
      navigate('/users');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Password mutation ──────────────────────────────────────────────────
  const passwordMutation = useMutation({
    mutationFn: async (values: PasswordFormValues) => {
      await apiClient.patch(`/users/${id}`, { password: values.new_password });
    },
    onSuccess: () => {
      toast.success('Password updated');
      passwordForm.reset({ new_password: '', confirm_password: '' });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Loading state (edit mode) ──────────────────────────────────────────
  if (isEdit && userLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/users')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEdit ? `Edit User — ${userData?.name ?? ''}` : 'Add User'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? 'Update user details and role assignment' : 'Create a new user account'}
          </p>
        </div>
      </div>

      {/* ── Main details card ─────────────────────────────────────────────── */}
      <div className="rounded-xl border bg-card p-6 space-y-5">
        <h2 className="text-base font-semibold">User Details</h2>

        {isEdit ? (
          /* ── Edit form ────────────────────────────────────────────────── */
          <form
            onSubmit={editForm.handleSubmit((v) => editMutation.mutate(v))}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                placeholder="John Doe"
                {...editForm.register('name')}
              />
              {editForm.formState.errors.name && (
                <p className="text-xs text-destructive">
                  {editForm.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                placeholder="user@example.com"
                {...editForm.register('email')}
              />
              {editForm.formState.errors.email && (
                <p className="text-xs text-destructive">
                  {editForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                {userData?.organization ? (
                  <>
                    <Label>Organisation Role</Label>
                    <div className="flex h-9 items-center rounded-md border bg-muted/50 px-3">
                      <Badge className={orgRoleBadgeClass(userData.org_role ?? null)}>
                        {userData.org_role ?? '—'}
                      </Badge>
                    </div>
                  </>
                ) : (
                  <>
                    <Label>Role</Label>
                    <Select value={selectedEditRole} onValueChange={handleEditRoleChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="No system role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">No system role</SelectItem>
                        {roles.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={editForm.watch('is_active') ? 'active' : 'inactive'}
                  onValueChange={(v) => editForm.setValue('is_active', v === 'active')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={editMutation.isPending}>
                {editMutation.isPending ? 'Saving…' : 'Update User'}
              </Button>
            </div>
          </form>
        ) : (
          /* ── Create form ──────────────────────────────────────────────── */
          <form
            onSubmit={createForm.handleSubmit((v) => createMutation.mutate(v))}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="create-name">Full Name</Label>
              <Input
                id="create-name"
                placeholder="John Doe"
                {...createForm.register('name')}
              />
              {createForm.formState.errors.name && (
                <p className="text-xs text-destructive">
                  {createForm.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="user@example.com"
                {...createForm.register('email')}
              />
              {createForm.formState.errors.email && (
                <p className="text-xs text-destructive">
                  {createForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-password">Password</Label>
              <Input
                id="create-password"
                type="password"
                placeholder="••••••••"
                {...createForm.register('password')}
              />
              {createForm.formState.errors.password && (
                <p className="text-xs text-destructive">
                  {createForm.formState.errors.password.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="create-org-name">Organisation Name</Label>
              <Input
                id="create-org-name"
                placeholder="Acme Corp"
                {...createForm.register('organization_name')}
              />
              {createForm.formState.errors.organization_name && (
                <p className="text-xs text-destructive">
                  {createForm.formState.errors.organization_name.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={createForm.watch('is_active') ? 'active' : 'inactive'}
                onValueChange={(v) => createForm.setValue('is_active', v === 'active')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/users')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating…' : 'Create User'}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* ── Change Password card (edit mode only) ──────────────────────────── */}
      {isEdit && (
        <div className="rounded-xl border bg-card p-6 space-y-5">
          <div>
            <h2 className="text-base font-semibold">Change Password</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Leave blank to keep the current password. Fill in both fields to set a new one.
            </p>
          </div>

          <form
            onSubmit={passwordForm.handleSubmit((v) => passwordMutation.mutate(v))}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="••••••••"
                {...passwordForm.register('new_password')}
              />
              {passwordForm.formState.errors.new_password && (
                <p className="text-xs text-destructive">
                  {passwordForm.formState.errors.new_password.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="••••••••"
                {...passwordForm.register('confirm_password')}
              />
              {passwordForm.formState.errors.confirm_password && (
                <p className="text-xs text-destructive">
                  {passwordForm.formState.errors.confirm_password.message}
                </p>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={passwordMutation.isPending}>
                {passwordMutation.isPending ? 'Updating…' : 'Update Password'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
